import { useMutation } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';

interface CategoryUpdateData {
  documentId: string;
  categoryId: string | null;
  categoryName: string | null;
}

// Document category update mutation
export const useUpdateDocumentCategory = (onSuccess?: (data: CategoryUpdateData) => void) => {
  const { keycloak } = useKeycloak();

  return useMutation({
    mutationFn: async ({ documentId, categoryId, categoryName: _ }: { 
      documentId: string; 
      categoryId: string | null;
      categoryName?: string | null;
    }) => {
      const response = await fetch(`${window.location.origin}/api/v1/dms/documents/${documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${keycloak.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          document_category_id: categoryId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update document category: ${response.status} ${response.statusText} - ${errorText}`);
      }

      return response.json();
    },

    // ✅ ON SUCCESS - Call parent with optimistic update data
    onSuccess: (_, variables) => {
      if (onSuccess) {
        onSuccess({
          documentId: variables.documentId,
          categoryId: variables.categoryId,
          categoryName: variables.categoryName || null,
        });
      }
    },

    // ❌ ON ERROR - Log error
    onError: (err) => {
      console.error('Document category update failed:', err);
    },
  });
};

// Document list query key factory
export const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params: any) => [...documentKeys.lists(), params] as const,
};

// Document categories query key factory  
export const categoryKeys = {
  all: ['document-categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
}; 